import { Injectable } from '@angular/core';
import { BehaviorSubject, skip } from 'rxjs';
import { point } from '@turf/helpers';
import { Feature, Polygon } from 'geojson';
import type { FillLayer, GeoJSONSource, LngLat } from 'mapbox-gl';
import circle from '@turf/circle';
import { MapBoxService } from '../../../../map/mapbox/mapbox.service';
import { ARBITRARY_CELL } from '../../../../map/mapbox/services/layer-store.service';

export type WKT = string;

@Injectable({
  providedIn: 'root',
})
export class RadiusSelectionService {
  public circleRadius: BehaviorSubject<number> = new BehaviorSubject<number>(6);

  private lastSelectionCenter!: LngLat;
  private _lastGeometry: Feature<Polygon> | null = null;

  public get lastGeometry(): Feature<Polygon> | null {
    return this._lastGeometry;
  }

  constructor(private mapboxService: MapBoxService) {
    this.circleRadius.pipe(skip(1)).subscribe(() => {
      this.renderCircle(this.lastSelectionCenter);
    });
  }

  /**
   * Asks the map whether the circle exists rather than keeping a flag: each
   * order form builds a new map, and a flag set against the last one was
   * read against the next.
   */
  public renderCircle(center: LngLat): void {
    this.lastSelectionCenter = center;
    if (!center) return;

    const map = this.mapboxService.reportsMap;
    if (map.getLayer(ARBITRARY_CELL)) {
      map.setPaintProperty(ARBITRARY_CELL, 'fill-opacity', 0.4);
      this.updateCircle(center);
    } else {
      this.addCircleLayer(center);
    }
  }

  private updateCircle(center: LngLat): void {
    const radius = this.circleRadius.getValue();
    const centerPoint = point([center.lng, center.lat]);

    const circleFeature = circle(centerPoint, radius, {
      units: 'miles',
    });
    this._lastGeometry = circleFeature;

    const source = (this.mapboxService.reportsMap.getSource(
      ARBITRARY_CELL
    ) as GeoJSONSource)!;

    if (source) {
      source.setData(circleFeature);
    }
  }

  private addCircleLayer(center: LngLat | null): void {
    const radius = this.circleRadius.getValue();
    const centerPoint = point([
      (center || this.lastSelectionCenter!).lng,
      (center || this.lastSelectionCenter!).lat,
    ]);

    const circleFeature = circle(centerPoint, radius, {
      units: 'miles',
    });
    this._lastGeometry = circleFeature;

    // @ts-ignore
    const circleSource: GeoJSONSource = {
      type: 'geojson',
      // @ts-ignore
      data: circleFeature,
    };

    if (!this.mapboxService.reportsMap.getSource(ARBITRARY_CELL)) {
      this.mapboxService.reportsMap.addSource(ARBITRARY_CELL, circleSource);
    }

    const circleLayer: FillLayer = {
      id: ARBITRARY_CELL,
      type: 'fill',
      source: ARBITRARY_CELL,
      paint: {
        'fill-color': '#add8e6',
        'fill-opacity': 0.4,
        'fill-outline-color': '#0000ff',
      },
    };

    this.mapboxService.reportsMap.addLayer(circleLayer);
  }

  public hideLayer(): void {
    if (this.mapboxService.reportsMap.getLayer(ARBITRARY_CELL)) {
      this.mapboxService.reportsMap.setPaintProperty(
        ARBITRARY_CELL,
        'fill-opacity',
        0
      );
    }
  }
}
